import * as CryptoJS from 'crypto-js';

const ENCRYPTED_VALUE_PREFIX = 'enc:v2:';
const ENCRYPTION_SECRET = 'dms-dt-v2::browser-storage::2026-08-29';
const KEYCLOAK_CALLBACK_PREFIX = 'kc-callback-';

interface EncryptedStorageValue {
  ciphertext: string;
  iv: string;
  mac: string;
}

let initialized = false;
let nativeGetItem: typeof Storage.prototype.getItem;
let nativeSetItem: typeof Storage.prototype.setItem;

const derivedKey = CryptoJS.PBKDF2(
  ENCRYPTION_SECRET,
  'dms-dt-v2::storage-key-derivation',
  {
    keySize: 512 / 32,
    iterations: 100_000,
    hasher: CryptoJS.algo.SHA256,
  },
);
const encryptionKey = CryptoJS.lib.WordArray.create(
  derivedKey.words.slice(0, 8),
  32,
);
const authenticationKey = CryptoJS.lib.WordArray.create(
  derivedKey.words.slice(8, 16),
  32,
);

function encrypt(value: string): string {
  const iv = CryptoJS.lib.WordArray.random(16);
  const ciphertext = CryptoJS.AES.encrypt(value, encryptionKey, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).ciphertext.toString(CryptoJS.enc.Base64);
  const ivString = iv.toString();

  const payload: EncryptedStorageValue = {
    ciphertext,
    iv: ivString,
    mac: CryptoJS.HmacSHA256(
      `${ivString}:${ciphertext}`,
      authenticationKey,
    ).toString(),
  };

  return ENCRYPTED_VALUE_PREFIX + JSON.stringify(payload);
}

function decrypt(value: string): string | null {
  if (!value.startsWith(ENCRYPTED_VALUE_PREFIX)) {
    return value;
  }

  try {
    const payload = JSON.parse(
      value.slice(ENCRYPTED_VALUE_PREFIX.length),
    ) as EncryptedStorageValue;
    const expectedMac = CryptoJS.HmacSHA256(
      `${payload.iv}:${payload.ciphertext}`,
      authenticationKey,
    ).toString();

    if (expectedMac !== payload.mac) {
      return null;
    }

    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: CryptoJS.enc.Base64.parse(payload.ciphertext),
    });

    return CryptoJS.AES.decrypt(cipherParams, encryptionKey, {
      iv: CryptoJS.enc.Hex.parse(payload.iv),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }).toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

function isProtocolStorageKey(key: string): boolean {
  // keycloak-js enumerates localStorage and parses these raw values directly
  // while validating OAuth state/PKCE. Encrypting them prevents the callback
  // adapter from finding the matching state and causes a login redirect loop.
  return key === 'kc-test' || key.startsWith(KEYCLOAK_CALLBACK_PREFIX);
}

/**
 * Encrypts all values written through the native localStorage and
 * sessionStorage APIs. Keys remain unchanged so removeItem(), key() and clear()
 * continue to behave normally. Existing plaintext values are encrypted when
 * first read.
 */
export function initializeEncryptedBrowserStorage(enabled = true): void {
  if (
    !enabled ||
    initialized ||
    typeof window === 'undefined' ||
    !window.Storage
  ) {
    return;
  }

  nativeGetItem = Storage.prototype.getItem;
  nativeSetItem = Storage.prototype.setItem;

  // Recover callback state written by the previous encrypted-storage version
  // so an in-flight login can complete after this fix is deployed.
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key || !isProtocolStorageKey(key)) {
      continue;
    }

    const storedValue = nativeGetItem.call(window.localStorage, key);
    if (storedValue?.startsWith(ENCRYPTED_VALUE_PREFIX)) {
      const plaintext = decrypt(storedValue);
      if (plaintext !== null) {
        nativeSetItem.call(window.localStorage, key, plaintext);
      }
    }
  }

  Storage.prototype.setItem = function (key: string, value: string): void {
    nativeSetItem.call(
      this,
      key,
      isProtocolStorageKey(key) ? String(value) : encrypt(String(value)),
    );
  };

  Storage.prototype.getItem = function (key: string): string | null {
    const storedValue = nativeGetItem.call(this, key);
    if (storedValue === null) {
      return null;
    }

    if (isProtocolStorageKey(key)) {
      return decrypt(storedValue);
    }

    const plaintext = decrypt(storedValue);
    if (plaintext !== null && !storedValue.startsWith(ENCRYPTED_VALUE_PREFIX)) {
      nativeSetItem.call(this, key, encrypt(plaintext));
    }

    return plaintext;
  };

  initialized = true;
}

/** Only intended for verification that the persisted representation is encrypted. */
export function getRawStorageValue(
  storage: Storage,
  key: string,
): string | null {
  return nativeGetItem
    ? nativeGetItem.call(storage, key)
    : Storage.prototype.getItem.call(storage, key);
}

/** Only intended for migration and tamper-handling tests. */
export function setRawStorageValue(
  storage: Storage,
  key: string,
  value: string,
): void {
  if (nativeSetItem) {
    nativeSetItem.call(storage, key, value);
    return;
  }

  Storage.prototype.setItem.call(storage, key, value);
}
